/**
 * Seção 'Contratos' do detalhe do cliente: tabela compacta (Nome | Tipo |
 * Valor | Vigência | Status) + atalho '+ Contrato' com o cliente preselecionado.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { MODALITY_LABELS } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, Skeleton } from '@/components/ui/misc';
import { contractsOptions, errorMessage } from './billing-queries';
import { formatBRL, formatVigencia } from './billing-format';
import { CONTRACT_STATUS_CHIP } from './contracts-tab';
import { ContractDrawer } from './contract-drawer';

export function ClientContractsSection({
  clientId,
  clientName,
}: {
  clientId: number;
  clientName: string;
}) {
  const contractsQ = useQuery(contractsOptions({ client_ids: [clientId], status: 'all' }));
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <section className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Contratos do cliente</h2>
          {contractsQ.data ? (
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
              {contractsQ.data.length}
            </span>
          ) : null}
        </div>
        <Button size="sm" onClick={() => setDrawerOpen(true)}>
          <Plus />
          Contrato
        </Button>
      </div>
      {contractsQ.isPending ? (
        <div aria-busy className="space-y-3 px-4 pb-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-4 w-1/4" />
              <Skeleton className="h-4 w-1/5" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-16 rounded-sm" />
            </div>
          ))}
        </div>
      ) : contractsQ.isError ? (
        <p className="px-4 pb-4 text-sm text-destructive">
          Erro ao carregar contratos: {errorMessage(contractsQ.error)}
        </p>
      ) : contractsQ.data.length === 0 ? (
        <EmptyState className="h-32" message="Nenhum contrato para este cliente" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left text-[13px]">
                <th className="h-9 px-4 font-semibold">Nome</th>
                <th className="h-9 px-4 font-semibold">Tipo</th>
                <th className="h-9 px-4 font-semibold">Valor</th>
                <th className="h-9 px-4 font-semibold">Vigência</th>
                <th className="h-9 px-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {contractsQ.data.map((c) => {
                const chip = CONTRACT_STATUS_CHIP[c.status];
                return (
                  <tr
                    key={c.id}
                    className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-56 truncate px-4 font-medium" title={c.name}>
                      {c.name}
                    </td>
                    <td className="px-4 whitespace-nowrap">
                      <span className="mr-2 text-muted-foreground">{c.contractTypeName}</span>
                      <Badge variant="primary">{MODALITY_LABELS[c.modality]}</Badge>
                    </td>
                    <td className="px-4 whitespace-nowrap tabular-nums">{formatBRL(c.value)}</td>
                    <td className="px-4 whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatVigencia(c.startDate, c.expirationDate)}
                    </td>
                    <td className="px-4 whitespace-nowrap">
                      <Badge variant={chip.variant}>{chip.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen ? (
        <ContractDrawer
          presetClient={{ id: clientId, name: clientName }}
          onClose={() => setDrawerOpen(false)}
        />
      ) : null}
    </section>
  );
}
